import React, { useEffect, useRef, useState, useCallback } from "react";
import { resolveApiUrl, resolveWsUrl } from "./api-url";
import { getActiveTenant, getPreferredTenant } from "@/lib/active-tenant";
import {
  WebSocketContext,
  type AdminUser,
  type WebSocketMessage,
} from "./websocket-context";

// Re-exports для обратной совместимости (старые импорты из @/providers/WebSocketProvider).
export {
  useWebSocket,
  type AdminUser,
  type AdminNotification,
  type LivestreamRealtimeType,
  type LivestreamRealtimeMessage,
  type WebSocketMessage,
  type WebSocketContextType,
} from "./websocket-context";

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [onlineAdmins, setOnlineAdmins] = useState<AdminUser[]>([]);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const [retryCount, setRetryCount] = useState(0);

  const API_URL = resolveApiUrl();
  const WS_URL = resolveWsUrl();

  const extractTokenExp = (token: string): number | null => {
    const parts = token.split(".");
    if (parts.length < 2) {
      return null;
    }
    try {
      const decoded = atob(parts[1]);
      const match = decoded.match(/"exp"\s*:\s*(\d+)/);
      if (!match) {
        return null;
      }
      const exp = Number(match[1]);
      return Number.isFinite(exp) ? exp : null;
    } catch {
      return null;
    }
  };

  const isTokenExpired = (token: string): boolean => {
    const exp = extractTokenExp(token);
    if (!exp) {
      return false;
    }
    return Date.now() >= exp * 1000;
  };

  const refreshAccessToken = async (): Promise<string | null> => {
    try {
      const tenant = getActiveTenant() ?? getPreferredTenant();
      const response = await fetch(`${API_URL}/auth/admin/refresh`, {
        method: "POST",
        credentials: "include",
        headers: tenant ? { "X-Tenant-Id": tenant } : undefined,
      });

      if (!response.ok) {
        localStorage.removeItem("accessToken");
        return null;
      }

      const payload = (await response.json()) as {
        success: boolean;
        data: { accessToken: string } | null;
      };

      if (!payload.success || !payload.data?.accessToken) {
        localStorage.removeItem("accessToken");
        return null;
      }

      localStorage.setItem("accessToken", payload.data.accessToken);
      return payload.data.accessToken;
    } catch {
      localStorage.removeItem("accessToken");
      return null;
    }
  };

  const connect = useCallback(async () => {
    let token = localStorage.getItem("accessToken");

    if (!token) {
      return;
    }

    if (isTokenExpired(token)) {
      token = await refreshAccessToken();
      if (!token) {
        return;
      }
    }

    const activeTenant = getActiveTenant();
    const tenantQuery = activeTenant ? `&tenant=${encodeURIComponent(activeTenant)}` : '';
    const wsUrl = `${WS_URL}/ws/notifications?token=${token}${tenantQuery}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log("UseWebSocket: Connected");
      setIsConnected(true);
      setRetryCount(0);
      setSocket(ws);

      ws.send(JSON.stringify({ type: "get_online_admins" }));

      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      heartbeatIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "pong" }));
        }
      }, 30_000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as WebSocketMessage;

        if (data.type === "ping") {
          ws.send(JSON.stringify({ type: "pong" }));
          return;
        }

        console.log("WebSocket received:", data.type, data);

        if (data.type === "online_admins") {
          setOnlineAdmins(data.admins);
        } else if (data.type === "admin_online") {
          const newAdmin = data.admin;
          setOnlineAdmins((prev) => {
            if (prev.find((a) => a.id === newAdmin.id)) {
              return prev;
            }
            return [...prev, newAdmin];
          });
        } else if (data.type === "admin_offline") {
          const adminId = Number(data.adminId);
          setOnlineAdmins((prev) => prev.filter((a) => a.id !== adminId));
        }

        setLastMessage(data);
      } catch (error) {
        console.error("WebSocket message parse error", error);
      }
    };

    ws.onclose = (event) => {
      console.log("UseWebSocket: Disconnected", event.code, event.reason);
      setIsConnected(false);
      setSocket(null);
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }

      if (
        token
        && retryCount < 5
        && event.code !== 1000
        && event.code !== 1005
        && event.code !== 4001
        && event.code !== 4002
        && event.code !== 4003
      ) {
        const timeout = Math.min(1000 * 2 ** retryCount, 30_000);
        reconnectTimeoutRef.current = setTimeout(() => {
          setRetryCount((prev) => prev + 1);
        }, timeout);
      }

      if (event.code === 4003) {
        void (async () => {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            setRetryCount(0);
          }
        })();
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error", error);
      ws.close();
    };

    return ws;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryCount, WS_URL]);

  useEffect(() => {
    let ws: WebSocket | undefined;
    void (async () => {
      ws = await connect();
    })();
    return () => {
      if (ws) {
        ws.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [connect]);

  const send = useCallback(
    (type: string, data?: Record<string, unknown>) => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type, ...data }));
      } else {
        console.warn("WebSocket not connected");
      }
    },
    [socket],
  );

  return (
    <WebSocketContext.Provider
      value={{ socket, isConnected, lastMessage, onlineAdmins, send }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
